import React, { useState, useEffect, useCallback } from 'react'
import { Search, Download, RefreshCw, TrendingUp, DollarSign, Package } from 'lucide-react'
import { DashboardData, ProductFilter } from '../types'
import { productsService } from '../services/productsService'
import { PieChart } from './PieChart'
import { BarChart } from './BarChart'

export const ProductsDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'accounts' | 'charts'>('overview')
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<ProductFilter>({})
  const [searchTerm, setSearchTerm] = useState('')
  const [accounts, setAccounts] = useState<string[]>([])
  const [productTypes, setProductTypes] = useState<string[]>([])
  const [exporting, setExporting] = useState(false)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [data, accountList, productList] = await Promise.all([
        productsService.getDashboardData(filter),
        productsService.getUniqueAccounts(),
        productsService.getUniqueProductTypes()
      ])
      setDashboardData(data)
      setAccounts(accountList)
      setProductTypes(productList)
    } catch (error) {
      console.error('Error loading products data:', error)
    } finally {
      setLoading(false)
    }
  }, [filter])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setFilter({ ...filter, search: searchTerm })
  }

  const handleExport = async () => {
    try {
      setExporting(true)
      const csvContent = await productsService.exportData(filter)
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `products-data-${new Date().toISOString().split('T')[0]}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    } catch (error) {
      console.error('Error exporting data:', error)
      alert('Failed to export data')
    } finally {
      setExporting(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const formatCompactCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `£${(amount / 1000000).toFixed(1)}M`
    } else if (amount >= 1000) {
      return `£${(amount / 1000).toFixed(0)}K`
    }
    return formatCurrency(amount)
  }

  if (loading && !dashboardData) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#f9fafb'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            animation: 'spin 1s linear infinite', 
            borderRadius: '50%', 
            height: '32px', 
            width: '32px', 
            border: '2px solid #3b82f6', 
            borderBottomColor: 'transparent',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
            Loading products dashboard...
          </p>
        </div>
      </div>
    )
  }

  if (!dashboardData) return null

  const pieChartData = dashboardData.product_summary.slice(0, 8).map(product => ({
    label: product.product_type,
    value: product.total_value
  }))

  const pieChartCountData = dashboardData.product_summary.slice(0, 8).map(product => ({
    label: product.product_type,
    value: product.total_count
  }))

  const barChartData = dashboardData.accounts.slice(0, 10).map(account => ({
    label: account.account,
    value: account.total_value
  }))

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
      {/* Header */}
      <div style={{ 
        background: 'white', 
        borderBottom: '1px solid #e5e7eb',
        padding: '24px'
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <div>
              <h1 style={{ 
                fontSize: '28px', 
                fontWeight: 'bold', 
                color: '#111827',
                fontFamily: 'Lexend Deca, sans-serif',
                marginBottom: '8px'
              }}>
                Products Dashboard
              </h1>
              <p style={{ 
                color: '#6b7280',
                fontFamily: 'Lexend Deca, sans-serif'
              }}>
                Comprehensive view of products sold across all accounts
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleExport}
                disabled={exporting || !dashboardData}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '8px 16px',
                  background: exporting ? '#f3f4f6' : '#10b981',
                  color: exporting ? '#6b7280' : 'white',
                  fontWeight: '500',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: exporting ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontFamily: 'Lexend Deca, sans-serif',
                  transition: 'all 0.2s'
                }}
              >
                <Download style={{ height: '16px', width: '16px', marginRight: '8px' }} />
                {exporting ? 'Exporting...' : 'Export CSV'}
              </button>
              <button
                onClick={loadData}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '8px 16px',
                  background: 'white',
                  color: '#374151',
                  fontWeight: '500',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontFamily: 'Lexend Deca, sans-serif',
                  transition: 'all 0.2s'
                }}
              >
                <RefreshCw style={{ height: '16px', width: '16px', marginRight: '8px' }} />
                Refresh
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ 
            display: 'flex', 
            borderBottom: '1px solid #e5e7eb'
          }}>
            <button
              onClick={() => setActiveTab('overview')}
              style={{
                padding: '12px 16px',
                background: 'transparent',
                color: activeTab === 'overview' ? '#2563eb' : '#6b7280',
                fontWeight: activeTab === 'overview' ? '600' : '500',
                border: 'none',
                borderBottom: activeTab === 'overview' ? '2px solid #2563eb' : '2px solid transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'Lexend Deca, sans-serif',
                transition: 'all 0.2s'
              }}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('accounts')}
              style={{
                padding: '12px 16px',
                background: 'transparent',
                color: activeTab === 'accounts' ? '#2563eb' : '#6b7280',
                fontWeight: activeTab === 'accounts' ? '600' : '500',
                border: 'none',
                borderBottom: activeTab === 'accounts' ? '2px solid #2563eb' : '2px solid transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'Lexend Deca, sans-serif',
                transition: 'all 0.2s'
              }}
            >
              By Account
            </button>
            <button
              onClick={() => setActiveTab('charts')}
              style={{
                padding: '12px 16px',
                background: 'transparent',
                color: activeTab === 'charts' ? '#2563eb' : '#6b7280',
                fontWeight: activeTab === 'charts' ? '600' : '500',
                border: 'none',
                borderBottom: activeTab === 'charts' ? '2px solid #2563eb' : '2px solid transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'Lexend Deca, sans-serif',
                transition: 'all 0.2s'
              }}
            >
              Charts & Analytics
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ 
        background: 'white', 
        borderBottom: '1px solid #e5e7eb',
        padding: '16px 24px'
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search deals, accounts..."
                  style={{
                    paddingLeft: '32px',
                    paddingRight: '12px',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Lexend Deca, sans-serif',
                    width: '200px'
                  }}
                />
                <Search style={{
                  position: 'absolute',
                  left: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  height: '16px',
                  width: '16px',
                  color: '#9ca3af'
                }} />
              </div>
              <button
                type="submit"
                style={{
                  padding: '8px 16px',
                  background: '#2563eb',
                  color: 'white',
                  fontWeight: '500',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontFamily: 'Lexend Deca, sans-serif'
                }}
              >
                Search
              </button>
            </form>

            <select
              value={filter.account || ''}
              onChange={(e) => setFilter({ ...filter, account: e.target.value || undefined })}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'Lexend Deca, sans-serif'
              }}
            >
              <option value="">All Accounts</option>
              {accounts.map(account => (
                <option key={account} value={account}>{account}</option>
              ))}
            </select>

            <select
              value={filter.product_type || ''}
              onChange={(e) => setFilter({ ...filter, product_type: e.target.value || undefined })}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'Lexend Deca, sans-serif'
              }}
            >
              <option value="">All Product Types</option>
              {productTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>

            {(filter.account || filter.product_type || filter.search) && (
              <button
                onClick={() => setFilter({})}
                style={{
                  padding: '8px 12px',
                  background: '#f3f4f6',
                  color: '#374151',
                  fontWeight: '500',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontFamily: 'Lexend Deca, sans-serif'
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '24px' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          {activeTab === 'overview' && (
            <>
              {/* KPI Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <Package style={{ height: '20px', width: '20px', color: '#10b981' }} />
                    <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif', margin: 0 }}>
                      Total Deals
                    </h3>
                  </div>
                  <p style={{ fontSize: '24px', fontWeight: '700', color: '#111827', fontFamily: 'Lexend Deca, sans-serif', margin: 0 }}>
                    {dashboardData.total_deals.toLocaleString()}
                  </p>
                </div>

                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <DollarSign style={{ height: '20px', width: '20px', color: '#3b82f6' }} />
                    <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif', margin: 0 }}>
                      Total Value
                    </h3>
                  </div>
                  <p style={{ fontSize: '24px', fontWeight: '700', color: '#111827', fontFamily: 'Lexend Deca, sans-serif', margin: 0 }}>
                    {formatCurrency(dashboardData.total_value)}
                  </p>
                </div>

                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <TrendingUp style={{ height: '20px', width: '20px', color: '#f59e0b' }} />
                    <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif', margin: 0 }}>
                      Total Commission
                    </h3>
                  </div>
                  <p style={{ fontSize: '24px', fontWeight: '700', color: '#111827', fontFamily: 'Lexend Deca, sans-serif', margin: 0 }}>
                    {formatCurrency(dashboardData.total_commission)}
                  </p>
                </div>
              </div>

              {/* Product Summary Table */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{ padding: '20px', borderBottom: '1px solid #e5e7eb' }}>
                  <h2 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#111827',
                    fontFamily: 'Lexend Deca, sans-serif',
                    margin: 0
                  }}>
                    Product Performance
                  </h2>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb' }}>
                        <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', fontFamily: 'Lexend Deca, sans-serif', borderBottom: '1px solid #e5e7eb' }}>
                          Product Type
                        </th>
                        <th style={{ padding: '12px 20px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#374151', fontFamily: 'Lexend Deca, sans-serif', borderBottom: '1px solid #e5e7eb' }}>
                          Count
                        </th>
                        <th style={{ padding: '12px 20px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#374151', fontFamily: 'Lexend Deca, sans-serif', borderBottom: '1px solid #e5e7eb' }}>
                          Total Value
                        </th>
                        <th style={{ padding: '12px 20px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#374151', fontFamily: 'Lexend Deca, sans-serif', borderBottom: '1px solid #e5e7eb' }}>
                          Commission
                        </th>
                        <th style={{ padding: '12px 20px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#374151', fontFamily: 'Lexend Deca, sans-serif', borderBottom: '1px solid #e5e7eb' }}>
                          % by Value
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.product_summary.map((product, index) => (
                        <tr key={product.product_type} style={{ borderBottom: index < dashboardData.product_summary.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                          <td style={{ padding: '12px 20px', fontSize: '14px', fontWeight: '500', color: '#111827', fontFamily: 'Lexend Deca, sans-serif' }}>
                            {product.product_type}
                          </td>
                          <td style={{ padding: '12px 20px', fontSize: '14px', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif', textAlign: 'right' }}>
                            {product.total_count}
                          </td>
                          <td style={{ padding: '12px 20px', fontSize: '14px', fontWeight: '600', color: '#10b981', fontFamily: 'Lexend Deca, sans-serif', textAlign: 'right' }}>
                            {formatCurrency(product.total_value)}
                          </td>
                          <td style={{ padding: '12px 20px', fontSize: '14px', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif', textAlign: 'right' }}>
                            {formatCurrency(product.total_commission)}
                          </td>
                          <td style={{ padding: '12px 20px', fontSize: '14px', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif', textAlign: 'right' }}>
                            {product.percentage_by_value.toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {activeTab === 'accounts' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {dashboardData.accounts.map((account) => (
                <div key={account.account} style={{
                  background: 'white',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                }}>
                  {/* Account Header */}
                  <div style={{
                    padding: '20px',
                    background: '#f9fafb',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#111827',
                        fontFamily: 'Lexend Deca, sans-serif',
                        margin: 0
                      }}>
                        {account.account}
                      </h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#10b981',
                            fontFamily: 'Lexend Deca, sans-serif'
                          }}>
                            {formatCurrency(account.total_value)}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#6b7280',
                            fontFamily: 'Lexend Deca, sans-serif'
                          }}>
                            Total Value
                          </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#3b82f6',
                            fontFamily: 'Lexend Deca, sans-serif'
                          }}>
                            {account.total_deals}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#6b7280',
                            fontFamily: 'Lexend Deca, sans-serif'
                          }}>
                            Deals
                          </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#f59e0b',
                            fontFamily: 'Lexend Deca, sans-serif'
                          }}>
                            {formatCurrency(account.total_commission)}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#6b7280',
                            fontFamily: 'Lexend Deca, sans-serif'
                          }}>
                            Commission
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Product Breakdown */}
                  <div style={{ padding: '20px' }}>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      fontFamily: 'Lexend Deca, sans-serif',
                      marginBottom: '16px'
                    }}>
                      Product Breakdown
                    </h4>
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {account.product_breakdown.map((product) => (
                        <div key={product.product_type} style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 16px',
                          background: '#f9fafb',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb'
                        }}>
                          <span style={{
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#111827',
                            fontFamily: 'Lexend Deca, sans-serif'
                          }}>
                            {product.product_type}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <span style={{
                              fontSize: '14px',
                              color: '#6b7280',
                              fontFamily: 'Lexend Deca, sans-serif'
                            }}>
                              {product.total_count} deals
                            </span>
                            <span style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#10b981',
                              fontFamily: 'Lexend Deca, sans-serif'
                            }}>
                              {formatCurrency(product.total_value)}
                            </span>
                            <span style={{
                              fontSize: '12px',
                              color: '#6b7280',
                              fontFamily: 'Lexend Deca, sans-serif',
                              minWidth: '40px',
                              textAlign: 'right'
                            }}>
                              {product.percentage_by_value.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'charts' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '24px' }}>
              {/* Product Type Pie Chart by Value */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
              }}>
                <PieChart
                  data={pieChartData}
                  title="Product Types by Value"
                  formatValue={formatCompactCurrency}
                />
              </div>

              {/* Product Type Pie Chart by Count */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
              }}>
                <PieChart
                  data={pieChartCountData}
                  title="Product Types by Deal Count"
                  formatValue={(value) => `${value} deals`}
                />
              </div>

              {/* Accounts Bar Chart */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                gridColumn: '1 / -1'
              }}>
                <BarChart
                  data={barChartData}
                  title="Account Performance by Total Value"
                  formatValue={formatCompactCurrency}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}